import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ChevronDown, Menu, X } from 'lucide-react';
import { PRODUCT_GROUPS, COMPARE_LINKS, RESOURCE_LINKS } from '../data/productNav';
import CountrySelector from './CountrySelector';
import { isIosApp } from '../utils/platform';

export const LandingHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const productsRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (productsRef.current && !productsRef.current.contains(e.target)) setProductsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setProductsOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, []);

  const closeAll = () => { setProductsOpen(false); setMobileOpen(false); };

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#064E3B]/10" data-testid="landing-header" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <img src="/logo-mark.png" alt="MoneyFeed" className="h-8 w-auto" />
            <span className="font-bold text-lg text-[#064E3B]">MoneyFeed</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Products mega-menu (click) */}
            <div className="relative" ref={productsRef} data-testid="header-products">
              <button
                onClick={() => setProductsOpen((v) => !v)}
                aria-expanded={productsOpen}
                aria-controls="products-mega-menu"
                aria-haspopup="menu"
                className="flex items-center gap-1 px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors"
              >
                Products <ChevronDown className={`h-4 w-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
              </button>
              {productsOpen && (
                <div className="absolute left-0 top-full pt-2" id="products-mega-menu" role="menu">
                  <div className="w-[36rem] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5">
                      {PRODUCT_GROUPS.map((group) => (
                        <div key={group.title}>
                          <div className="flex items-center gap-1.5 mb-2">
                            <group.icon className="h-3.5 w-3.5 text-[#84CC16]" />
                            <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{group.title}</span>
                          </div>
                          <div className="space-y-0.5">
                            {group.items.map((p) => (
                              <Link key={p.to} to={p.to} onClick={closeAll} data-testid={`header-product-${p.to}`}
                                className="flex items-start gap-2.5 px-2 py-1.5 rounded-lg hover:bg-[#F0FDF4] transition-colors group">
                                <p.icon className="h-4 w-4 text-[#064E3B]/70 mt-0.5 group-hover:text-[#064E3B]" />
                                <span>
                                  <span className="block text-sm font-semibold text-[#064E3B] leading-tight">{p.label}</span>
                                  <span className="block text-xs text-gray-500">{p.desc}</span>
                                </span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Compare row */}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mr-1">Compare:</span>
                      {COMPARE_LINKS.map((c) => (
                        <Link key={c.to} to={c.to} onClick={closeAll} data-testid={`header-compare-${c.to}`}
                          className="text-xs font-semibold text-[#064E3B] px-2.5 py-1 rounded-full bg-[#F0FDF4] hover:bg-[#84CC16]/20 transition-colors">
                          {c.label}
                        </Link>
                      ))}
                    </div>
                    {/* Resources row */}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mr-1">Learn:</span>
                      {RESOURCE_LINKS.map((r) => (
                        <Link key={r.to} to={r.to} onClick={closeAll} data-testid={`header-resource-${r.to}`}
                          className="text-xs font-semibold text-[#064E3B] px-2.5 py-1 rounded-full bg-[#F0FDF4] hover:bg-[#84CC16]/20 transition-colors">
                          {r.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <a href="/#features" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors">Features</a>
            <Link to="/tools" data-testid="header-tools-link" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors">Free Tools</Link>
            <Link to="/demo" data-testid="header-demo-link" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors">See Demo</Link>
            <Link to="/for-accountants" data-testid="header-for-accountants-link" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors">For Accountants</Link>
            <Link to="/blog" data-testid="header-blog-link" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors">Blog</Link>
            {!isIosApp() && <Link to="/pricing" data-testid="header-pricing-link" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors">Pricing</Link>}
          </nav>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-2">
            <CountrySelector />
            <Link to="/auth" className="px-4 py-2 text-sm font-semibold text-[#064E3B] hover:text-[#84CC16] transition-colors" data-testid="header-signin">Sign in</Link>
            <Link to="/auth"><Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-5" data-testid="header-get-started">Get started free</Button></Link>
          </div>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-2">
            <CountrySelector />
            <button className="text-[#064E3B]" onClick={() => setMobileOpen((v) => !v)} data-testid="header-mobile-toggle" aria-label="Menu">
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white max-h-[80vh] overflow-y-auto" data-testid="header-mobile-panel">
          <div className="container mx-auto px-4 py-3 space-y-4">
            {PRODUCT_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="px-3 pt-1 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                  <group.icon className="h-3.5 w-3.5 text-[#84CC16]" /> {group.title}
                </p>
                {group.items.map((p) => (
                  <Link key={p.to} to={p.to} onClick={closeAll}
                    className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">
                    {p.label}
                  </Link>
                ))}
              </div>
            ))}
            <div>
              <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-gray-400">Compare</p>
              {COMPARE_LINKS.map((c) => (
                <Link key={c.to} to={c.to} onClick={closeAll} className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">{c.label}</Link>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-2">
              <a href="/#features" onClick={closeAll} className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">Features</a>
              <Link to="/tools" onClick={closeAll} data-testid="header-mobile-tools-link" className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">Free Tools</Link>
              <Link to="/for-accountants" onClick={closeAll} data-testid="header-mobile-for-accountants-link" className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">For Accountants</Link>
              {RESOURCE_LINKS.map((r) => (
                <Link key={r.to} to={r.to} onClick={closeAll} className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">{r.label}</Link>
              ))}
              {!isIosApp() && <Link to="/pricing" onClick={closeAll} data-testid="header-mobile-pricing-link" className="block px-3 py-2 rounded-lg text-sm font-semibold text-[#064E3B] hover:bg-[#F0FDF4]">Pricing</Link>}
            </div>
            <div className="pt-1 flex gap-2">
              <Link to="/auth" onClick={closeAll} className="flex-1"><Button variant="outline" className="w-full rounded-full">Sign in</Button></Link>
              <Link to="/auth" onClick={closeAll} className="flex-1"><Button className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full">Get started</Button></Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default LandingHeader;
